import WixEvent from 'ui/events/WixEvent';
import quickActionBarUtils from '../../privates/utils/quickActionBarUtils';

/**
 * @callback $w.QuickActionBarItemClickedEventHandler
 * @summary Handles events fired when an item in a quick action bar is clicked.
 * @param {$w.QuickActionBarItemClickedEvent} event The event that has occurred.
 * @param {$w.$w} $w A selector function. The $w function enables event handlers
 *  to work with elements in [repeaters]($w.Repeater.html#repeating-callback-selector)
 *  and in the [global scope]($w.Repeater.html#regular-callback-selector).
 */

/**
 * @class QuickActionBarItemClickedEvent
 * @summary Event that is fired when a quick action bar item is clicked.
 * @memberof $w
 * @mixes $w.Event
 */
export default class QuickActionBarItemClickedEvent extends WixEvent {
    constructor(target, event) {
        super(target, event);

        this.item = quickActionBarUtils.convertToUserFormat(event.item);
        this.itemIndex = event.actionIndex;
    }

    /**
     * @typedef $w.QuickActionBarItemClickedEvent~QuickActionBarItem
     * @summary An object representing a quick action bar item.
     * @description
     *  The `itemType` is one of:
     *  + `"3dots"`
     *  + `"3dotsvertical"`
     *  + `"about"`
     *  + `"add"`
     *  + `"address"`
     *  + `"arrowdown"`
     *  + `"arrowup"`
     *  + `"blog"`
     *  + `"booking"`
     *  + `"briefcase"`
     *  + `"clock"`
     *  + `"clothes"`
     *  + `"cloud"`
     *  + `"contactform"`
     *  + `"creditcard"`
     *  + `"crown"`
     *  + `"currency"`
     *  + `"earth"`
     *  + `"ecom"`
     *  + `"email"`
     *  + `"facebook"`
     *  + `"flag"`
     *  + `"flickr"`
     *  + `"freebutton"`
     *  + `"gallery"`
     *  + `"googleplus"`
     *  + `"hamburgermenu"`
     *  + `"heart"`
     *  + `"home"`
     *  + `"hot"`
     *  + `"hotel"`
     *  + `"hotsale"`
     *  + `"icecream"`
     *  + `"instagram"`
     *  + `"lightning"`
     *  + `"link"`
     *  + `"linkedin"`
     *  + `"music"`
     *  + `"new"`
     *  + `"page"`
     *  + `"phone"`
     *  + `"pinterest"`
     *  + `"present"`
     *  + `"register"`
     *  + `"restaurant"`
     *  + `"rocket"`
     *  + `"sale"`
     *  + `"search"`
     *  + `"shoppingcart"`
     *  + `"signup"`
     *  + `"smile"`
     *  + `"social"`
     *  + `"speaker"`
     *  + `"star"`
     *  + `"ticket"`
     *  + `"twitter"`
     *  + `"vimeo"`
     *  + `"vk"`
     *  + `"yelp"`
     *  + `"youtube"`
     * @property {external:String} itemType The quick action bar item type.
     * @property {external:String} label The quick action bar item label.
     * @property {external:String} link The quick action bar item link.
     */

    /**
     * @member item
     * @label property
     * @syntax
     * get item(): QuickActionBarItem
     * @summary Gets the gallery item.
     * @snippet [QuickActionBarItemClickedEvent-item.es6=Get the quick action bar item that was clicked]
     * @type {$w.QuickActionBarItemClickedEvent~QuickActionBarItem}
     * @memberof $w.QuickActionBarItemClickedEvent
     * @readonly
     */

    /**
     * @member itemIndex
     * @label property
     * @syntax
     * get itemIndex(): Number
     * @summary Gets the quick action bar item's index.
     * @snippet [QuickActionBarItemClickedEvent-itemIndex.es6=Get the index of the quick action bar item that was clicked]
     * @type {external:Number}
     * @memberof $w.QuickActionBarItemClickedEvent
     * @readonly
     */

}



// WEBPACK FOOTER //
// ./js/modules/ui/events/QuickActionBarItemClickedEvent.es6